import { useState, useEffect } from "react";
import { Accordion, AccordionItem } from "@heroui/accordion";
import { Spinner } from "@heroui/spinner";
import axios from "axios";

export default function FinancialStatements({ ticker }) {
  const [incomeStatement, setIncomeStatement] = useState([]);
  const [balanceSheet, setBalanceSheet] = useState([]);
  const [cashFlow, setCashFlow] = useState([]);
  const [reportedCurrency, setReportedCurrency] = useState("");
  const [loading, setLoading] = useState({
    income: false,
    balance: false,
    cash: false,
  });
  const [expanded, setExpanded] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async (type) => {
      setLoading((prev) => ({ ...prev, [type]: true }));
      setIsLoading(true);
      try {
        const response = await axios.post(
          "/api/company/financials",
          {
            ticker: ticker,
            type: type,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        let fetchedData = response.data || [];
        // Assuming you want to reverse the order so the earliest comes first:
        fetchedData = fetchedData.reverse();

        if (type === "income") {
          setIncomeStatement(fetchedData);
          setReportedCurrency(fetchedData[0]?.reportedCurrency || "");
        } else if (type === "balance-sheet") {
          setBalanceSheet(fetchedData);
        } else if (type === "cash-flow") {
          setCashFlow(fetchedData);
        }
      } catch (error) {
        console.error("Error fetching financial data:", error);
      } finally {
        setLoading((prev) => ({ ...prev, [type]: false }));
        setIsLoading(false);
      }
    };

    fetchData("income");
    fetchData("balance-sheet");
    fetchData("cash-flow");
  }, [ticker]); // Fetch data when the component mounts or ticker changes

  const mainAccounts = new Set([
    "revenue",
    "grossProfit",
    "operatingIncome",
    "cashAndShortTermInvestments",
    "totalCurrentAssets",
    "totalAssets",
    "totalCurrentLiabilities",
    "totalEquity",
    "totalLiabilities",
    "netIncome",
    "depreciationAndAmortization",
    "netCashProvidedByOperatingActivities",
    "netCashUsedForInvestingActivites",
    "dividendsPaid",

    // Add other main accounts here
  ]);

  const isMainAccount = (field) => mainAccounts.has(field);

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";

    const isNegative = num < 0;
    let formattedNum = Math.abs(num);

    // Check if the number is a whole number
    const isWholeNumber = num % 1 === 0;

    if (formattedNum >= 1000000) {
      formattedNum =
        (formattedNum / 1000000).toFixed(isWholeNumber ? 0 : 2) + "M";
    } else if (formattedNum >= 1000) {
      formattedNum = (formattedNum / 1000).toFixed(isWholeNumber ? 0 : 2) + "K";
    } else {
      formattedNum = isWholeNumber
        ? parseInt(formattedNum)
        : formattedNum.toFixed(2);
    }

    formattedNum = formattedNum
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return isNegative ? `- $ ${formattedNum}` : `$ ${formattedNum}`;
  };

  const formatParamName = (paramName) => {
    const formattedName = paramName
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());

    // Apply bold and bottom border styling if it's a main account
    if (isMainAccount(paramName)) {
      return <span className="font-bold ">{formattedName}</span>;
    }

    return formattedName;
  };

  const renderTable = (data, fields) => {
    // Function to calculate YoY change
    const calculateYoYChange = (current, previous) => {
      if (previous === 0) {
        if (current === 0) return "0%"; // No change if both are 0
        return "N/A"; // Cannot calculate percentage change if previous year is 0
      }
      if (!previous || (!current && current !== 0)) return "-"; // Handle null or undefined values
      const change = ((current - previous) / previous) * 100;
      return `${change.toFixed(2)}%`; // Correctly formats the number to 2 decimal places
    };

    const renderYoYChangeColumn = (field) => {
      if (data.length > 1) {
        // Ensure there are at least two years of data to compare
        const currentYearValue = data[data.length - 1][field]; // Most recent year
        const previousYearValue = data[data.length - 2][field]; // Year before the most recent
        return calculateYoYChange(currentYearValue, previousYearValue);
      }
      return "-";
    };

    // Ensure data is correctly formatted and processed

    return (
      <div className="overflow-x-auto mb-4">
        <table className="min-w-full leading-normal text-sm mb-4">
          <thead>
            <tr>
              <th className="text-left "></th>
              {data.map((item, index) => (
                <th
                  key={index}
                  className="text-center whitespace-nowrap p-2 md:p-4"
                >
                  {item.date}
                </th>
              ))}
              <th className="text-center">YoY Change</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => (
              <tr
                key={field}
                className={`${
                  isMainAccount(field) ? "border-b" : ""
                } hover:bg-gray-100`}
              >
                <td
                  className={`text-left ${
                    isMainAccount(field) ? "font-bold" : ""
                  }`}
                >
                  {formatParamName(field)}
                </td>
                {data.map((item, index) => (
                  <td
                    key={index}
                    className={`text-center ${
                      isMainAccount(field) ? "font-bold" : ""
                    }`}
                  >
                    {formatNumber(item[field])}
                  </td>
                ))}
                <td
                  className={`text-center ${
                    isMainAccount(field) ? "font-bold" : ""
                  }`}
                >
                  {renderYoYChangeColumn(field)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    (<section
      id="financials"
      className="p-5 bg-white shadow-md rounded-lg border mb-4 mt-4"
    >
      <h2 className="text-xl font-bold mb-4">Financial Statements</h2>
      <Accordion>
        <AccordionItem
          title="Income Statement"
          expanded={expanded === "Income Statement"}
          onClick={() =>
            setExpanded(
              expanded === "Income Statement" ? null : "Income Statement"
            )
          }
        >
          {loading.income ? (
            <div className="flex justify-center items-center h-full">
              <Spinner />
            </div>
          ) : incomeStatement.length > 0 ? (
            <div>
              <p className="text-sm italic">
                In Millions of the reported currency ({reportedCurrency})
              </p>
              {renderTable(incomeStatement, [
                "revenue",
                "costOfRevenue",
                "grossProfit",
                "researchAndDevelopmentExpenses",
                "operatingExpenses",
                "operatingIncome",
                "ebitda",
                "netIncome",
                "eps",
              ])}
            </div>
          ) : (
            (<p className="text-center mb-10 ">No income statement data available.
                          </p>) // Display this when there's no data
          )}
        </AccordionItem>
        <AccordionItem
          title="Balance Sheet"
          expanded={expanded === "Balance Sheet"}
          onClick={() =>
            setExpanded(expanded === "Balance Sheet" ? null : "Balance Sheet")
          }
        >
          {loading.balance ? (
            <div className="flex justify-center items-center h-full">
              <Spinner />
            </div>
          ) : balanceSheet.length > 0 ? (
            <div>
              <p className="text-sm italic">
                In Millions of the reported currency ({reportedCurrency})
              </p>
              {renderTable(balanceSheet, [
                "cashAndCashEquivalents",
                "shortTermInvestments",
                "cashAndShortTermInvestments",
                "netReceivables",
                "inventory",
                "totalCurrentAssets",
                "propertyPlantEquipmentNet",
                "longTermInvestments",
                "totalAssets",
                "shortTermDebt",
                "totalCurrentLiabilities",
                "longTermDebt",
                "totalLiabilities",
                "totalEquity",
                "totalInvestments",
                "totalDebt",
              ])}
            </div>
          ) : (
            (<p className="text-center mb-10 ">
              {" "}No Balance Sheet data available.
                          </p>) // Display this when there's no data
          )}
        </AccordionItem>
        <AccordionItem
          title="Cash Flow"
          expanded={expanded === "Cash Flow"}
          onClick={() =>
            setExpanded(expanded === "Cash Flow" ? null : "Cash Flow")
          }
        >
          {loading.cash ? (
            <div className="flex justify-center items-center h-full">
              <Spinner />
            </div>
          ) : cashFlow.length > 0 ? (
            <div>
              <p className="text-sm italic">
                In Millions of the reported currency ({reportedCurrency})
              </p>
              {renderTable(cashFlow, [
                "netIncome",
                "depreciationAndAmortization",
                "inventory",
                "accountsPayables",
                "otherWorkingCapital",
                "netCashUsedForInvestingActivites",
                "debtRepayment",
                "dividendsPaid",
                "netCashProvidedByOperatingActivities",
                "netChangeInCash",
                "cashAtEndOfPeriod",
                "operatingCashFlow",
                "freeCashFlow",
                "capitalExpenditure",
              ])}
            </div>
          ) : (
            (<p className="text-center mb-10 ">No Cash Flow data available.</p>) // Display this when there's no data
          )}
        </AccordionItem>
      </Accordion>
    </section>)
  );
}
